import { useDeferredValue, useMemo, useState } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import { FactList, Faq, Prose, Section, linkClass } from '@/components/converter/PageParts';
import {
  Checkbox,
  HowTo,
  MoreTools,
  NextSteps,
  ToolCard,
  ToolHead,
  ToolHero,
  useLocalOnlyBreadcrumbs,
  useMounted,
  type FaqItem,
} from '@/components/svg-tools/ToolParts';
import { SvgInput } from '@/components/svg-tools/SvgInput';
import { CodeOutput } from '@/components/svg-tools/CodeOutput';
import { SAMPLE } from '@/components/svg-tools/sample';
import { normalizeSvg, svgImageSrc } from '@/utils/svg-tools/svg-source';
import { defaultJsxOptions, svgToJsx, toComponentName, type JsxOptions } from '@/utils/svg-tools/svg-to-jsx';

const PATH = '/svg-to-react';
const TITLE = 'SVG to JSX: Convert SVG to a React Component (TSX) | SVG.new';
const DESCRIPTION =
  'Paste an SVG, get a typed React component: React prop names, style objects, href instead of xlink:href and unique ids. Free, and it runs in your browser.';

const code = 'px-1.5 py-0.5 bg-cream-100 rounded text-[13px] font-mono [font-variant-ligatures:none] text-ink-soft';

const faq: FaqItem[] = [
  {
    q: 'How do I use the React component?',
    a: 'Save the code as a .tsx file named after the component, for example MoonBadge.tsx, import it and render it like any element: <MoonBadge className="h-6 w-6" aria-hidden />. Every prop you pass goes to the <svg> element, so className, width, height, style, aria attributes and event handlers all work, and so does ref on React 19.',
  },
  {
    q: 'Why are some ids rewritten with useId?',
    a: 'When the same SVG appears twice on a page, both copies define the same gradient, clip path or mask ids, and the browser uses the first one it finds. If that copy is hidden, the second one loses its gradient. useId() gives each instance its own prefix. Only ids that the SVG refers to itself are changed; turn the option off to keep every id as it is.',
  },
  {
    q: 'What happens to xlink:href?',
    a: 'It becomes href, which every current browser supports in SVG, and the xmlns:xlink declaration is dropped. Other xlink and xml attributes become React props such as xlinkTitle and xmlSpace.',
  },
  {
    q: 'What does the converter remove?',
    a: 'Things that do not belong in a component or that React would reject: <script> elements, event handler attributes such as onclick (pass functions as props instead), javascript: URLs, comments, editor metadata from Inkscape, Illustrator and Sketch, and enable-background. The notes under the output list what was changed, and warn if the SVG still links to outside files, which load when the component renders.',
  },
  {
    q: 'Is my SVG uploaded?',
    a: 'No. The conversion runs in your browser, and the SVG and the component code never leave your computer.',
  },
];

export default function SvgToReactPage() {
  useLocalOnlyBreadcrumbs();
  const mounted = useMounted();
  const [text, setText] = useState(SAMPLE.text);
  const [name, setName] = useState(toComponentName(SAMPLE.name));
  const [options, setOptions] = useState<Omit<JsxOptions, 'componentName'>>({
    typescript: defaultJsxOptions.typescript,
    uniqueIds: defaultJsxOptions.uniqueIds,
    removeDimensions: defaultJsxOptions.removeDimensions,
  });
  const deferred = useDeferredValue(text);
  const result = useMemo(
    () => (mounted ? svgToJsx(deferred, { ...options, componentName: name }) : null),
    [mounted, deferred, options, name]
  );
  const preview = useMemo(() => (result?.ok ? svgImageSrc(normalizeSvg(deferred).text) : null), [result, deferred]);
  const componentName = toComponentName(name);
  const ext = options.typescript ? 'tsx' : 'jsx';

  return (
    <>
      <ToolHead
        path={PATH}
        title={TITLE}
        description={DESCRIPTION}
        socialTitle="SVG to JSX and React component converter"
        appName="SVG.new SVG to React converter"
        crumb="SVG to React"
        featureList={[
          'Convert SVG to JSX and a typed React component (TSX or JSX)',
          'class to className and kebab-case to camelCase attribute names',
          'Inline style strings to style objects',
          'xlink:href to href',
          'Unique gradient and clip path ids per instance with useId',
          'Removes scripts, event handler attributes and editor metadata',
        ]}
        faq={faq}
      />
      <div className="min-h-screen bg-cream-50 flex flex-col font-sans">
        <SiteHeader />

        <main className="flex-1">
          <ToolHero
            crumb="SVG to React"
            before="SVG to "
            highlight="React"
            after=" component"
            intro="Convert SVG to JSX: paste the code or open a file and get a typed React component, with React prop names, style objects and ids that stay unique on every instance."
          >
            <ToolCard>
              <div className="grid min-w-0 lg:grid-cols-2">
                <div className="min-w-0 border-b border-cream-200 lg:border-b-0 lg:border-r">
                  <SvgInput
                    value={text}
                    sample={SAMPLE}
                    heightClass="h-[220px] sm:h-[360px]"
                    onChange={(next, fileName) => {
                      setText(next);
                      if (fileName) setName(toComponentName(fileName));
                    }}
                    footer={
                      <div className="flex items-center gap-3 border-t border-cream-200 px-4 py-3">
                        <div className="checker-bg flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md border border-cream-200">
                          {preview && (
                            // eslint-disable-next-line @next/next/no-img-element -- a data: URL of the visitor's SVG
                            <img src={preview} alt="Preview of the SVG" className="max-h-12 max-w-12 object-contain" />
                          )}
                        </div>
                        <p className="text-[12.5px] leading-relaxed text-ink-muted">
                          {result && !result.ok
                            ? result.error
                            : 'Preview of your SVG, drawn as an image so nothing in it runs.'}
                        </p>
                      </div>
                    }
                  />
                </div>
                <div className="min-w-0 p-4 space-y-3">
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                    <label className="flex items-center gap-2 text-[13px] font-medium text-ink">
                      Component
                      <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onBlur={() => setName(componentName)}
                        spellCheck={false}
                        className="h-8 w-[150px] rounded-md border border-cream-300 bg-white px-2 font-mono text-[12.5px] text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-sky"
                      />
                    </label>
                    <Checkbox checked={options.typescript} onChange={(v) => setOptions({ ...options, typescript: v })}>
                      TypeScript
                    </Checkbox>
                    <Checkbox checked={options.uniqueIds} onChange={(v) => setOptions({ ...options, uniqueIds: v })}>
                      Unique ids (useId)
                    </Checkbox>
                    <Checkbox
                      checked={options.removeDimensions}
                      onChange={(v) => setOptions({ ...options, removeDimensions: v })}
                    >
                      Remove width and height
                    </Checkbox>
                  </div>
                  {result?.ok ? (
                    <>
                      <CodeOutput
                        label={`${componentName}.${ext}`}
                        code={result.code}
                        download={{ fileName: `${componentName}.${ext}`, type: 'text/plain' }}
                        heightClass="max-h-[340px] sm:max-h-[420px]"
                      />
                      {result.notes.length > 0 && (
                        <ul className="list-disc space-y-1 pl-5 text-[12.5px] leading-relaxed text-ink-muted" aria-label="What changed">
                          {result.notes.map((note) => (
                            <li key={note}>{note}</li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <p className="rounded-xl border border-cream-200 bg-cream-50 px-4 py-6 text-[13px] text-ink-muted">
                      {result ? result.error : 'The component appears here.'}
                    </p>
                  )}
                </div>
              </div>
            </ToolCard>
          </ToolHero>

          <NextSteps
            agentText={
              <>
                Ask your agent to vectorize logo.png and turn the result into a React component. With the SVG.new MCP
                server added, Claude Code or Codex does the vectorizing step itself, from PNG, JPG or WebP.
              </>
            }
          />

          <Section heading="How to convert SVG to JSX">
            <HowTo
              steps={[
                {
                  title: 'Paste the SVG or open the file',
                  text: 'Paste markup copied from Figma, Illustrator, an icon set or a web page, or drop an .svg file on the code box. SVG copied out of HTML often lacks xmlns; the converter adds it.',
                },
                {
                  title: 'Name the component and pick the options',
                  text: 'The name comes from the file name, and you can change it. Leave TypeScript on for a .tsx file typed with SVGProps<SVGSVGElement>, or turn it off for plain JSX. Remove width and height if CSS or props should set the size.',
                },
                {
                  title: 'Copy or download it',
                  text: (
                    <>
                      Save it in your components folder, import it and pass props:{' '}
                      <code className={code}>{'<MoonBadge className="h-6 w-6" />'}</code>.
                    </>
                  ),
                },
              ]}
            />
          </Section>

          <Section heading="What changes between SVG and JSX" tone="cream">
            <FactList
              items={[
                {
                  term: 'Attribute names',
                  text: (
                    <>
                      <code className={code}>class</code> becomes <code className={code}>className</code>, and
                      hyphenated presentation attributes are camelCased the way React spells them:{' '}
                      <code className={code}>stroke-width</code> becomes <code className={code}>strokeWidth</code>,{' '}
                      <code className={code}>xml:space</code> becomes <code className={code}>xmlSpace</code>. data-* and
                      aria-* attributes stay as they are.
                    </>
                  ),
                },
                {
                  term: 'Inline styles',
                  text: (
                    <>
                      <code className={code}>style=&quot;fill:#fff; opacity:.85&quot;</code> becomes{' '}
                      <code className={code}>{'style={{ fill: "#fff", opacity: ".85" }}'}</code>. Values stay strings,
                      so nothing gains a px unit; CSS custom properties are kept.
                    </>
                  ),
                },
                {
                  term: 'xlink:href',
                  text: 'Becomes href, and the xlink namespace declaration is dropped.',
                },
                {
                  term: 'Ids',
                  text: 'Ids used by url(#…), href="#…", aria-labelledby or a <style> block get a per-instance prefix from useId(), and every reference is rewritten to match.',
                },
                {
                  term: 'Props',
                  text: 'The root <svg> ends with {...props}, so anything you pass overrides the attributes in the file.',
                },
              ]}
            />
            <div className="mt-6">
              <Prose>
                <p>
                  A file exported from a design tool converts more cleanly after a pass through the{' '}
                  <Link href="/svg-viewer" className={linkClass}>SVG optimizer</Link>: it removes metadata, shortens
                  path data and, where it can, moves the rules of a &lt;style&gt; block onto the shapes, so the component
                  carries fewer global class names.
                </p>
              </Prose>
            </div>
          </Section>

          <Section heading="Questions about SVG to React">
            <Faq items={faq} />
          </Section>

          <MoreTools current={PATH} />
        </main>

        <SiteFooter />
      </div>
    </>
  );
}
